/**
 * TG Activity Log service
 * Tracks subcontractor page views and logins
 */

import { supabase } from '../lib/supabase'
import type {
  TGActivityLogEntryWithSubcontractor,
  TGActivitySummary,
  TGActionType,
  TGPageName
} from '../types/tgActivity'
import { PAGE_NAME_LABELS } from '../types/tgActivity'

// ============================================
// Activity Logging (called from TG pages)
// ============================================

// Debounce tracking to prevent duplicate logs (React StrictMode, HMR, etc.)
const DEBOUNCE_MS = 5000 // 5 seconds

// In-memory lock to prevent race conditions (synchronous check)
const pendingLogs = new Set<string>()
const loggedRecently = new Map<string, number>()

function shouldLogActivity(key: string): boolean {
  const now = Date.now()

  // Check if already logged recently
  const lastLog = loggedRecently.get(key)
  if (lastLog && now - lastLog < DEBOUNCE_MS) {
    return false
  }

  // Check if currently being processed (race condition prevention)
  if (pendingLogs.has(key)) {
    return false
  }

  // Mark as pending IMMEDIATELY (synchronous)
  pendingLogs.add(key)
  return true
}

function markLogComplete(key: string): void {
  pendingLogs.delete(key)
  loggedRecently.set(key, Date.now())

  // Clean up old entries
  const cutoff = Date.now() - DEBOUNCE_MS
  for (const [k, time] of loggedRecently.entries()) {
    if (time < cutoff) {
      loggedRecently.delete(k)
    }
  }
}

export async function logTGActivity(
  subcontractorId: string,
  actionType: TGActionType,
  options?: {
    pageName?: TGPageName
    pagePath?: string
    entityId?: string
    metadata?: Record<string, unknown>
  }
): Promise<void> {
  // Create a unique key for this activity
  const logKey = `${subcontractorId}:${actionType}:${options?.pageName || ''}:${options?.entityId || ''}`

  if (!shouldLogActivity(logKey)) {
    return // Skip duplicate within debounce window
  }

  try {
    const { data: { user } } = await supabase.auth.getUser()

    const { error } = await supabase
      .from('tg_activity_log')
      .insert({
        subcontractor_id: subcontractorId,
        user_id: user?.id,
        action_type: actionType,
        page_name: options?.pageName,
        page_path: options?.pagePath,
        entity_id: options?.entityId,
        metadata: options?.metadata
      })

    if (error) {
      console.error('Error logging TG activity:', error)
    }
  } catch (error) {
    console.error('Error in logTGActivity:', error)
  } finally {
    // Always mark as complete to allow future logs after debounce period
    markLogComplete(logKey)
  }
}

export async function logPageView(
  subcontractorId: string,
  pageName: TGPageName,
  pagePath: string,
  entityId?: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  return logTGActivity(subcontractorId, 'page_view', {
    pageName,
    pagePath,
    entityId,
    metadata
  })
}

export async function logLogin(subcontractorId: string): Promise<void> {
  return logTGActivity(subcontractorId, 'login', {
    pageName: 'dashboard'
  })
}

// ============================================
// Activity Fetching (Admin)
// ============================================

export async function getTGActivityLog(
  options?: {
    subcontractorId?: string
    actionType?: TGActionType
    pageName?: TGPageName
    startDate?: string
    endDate?: string
    limit?: number
    offset?: number
  }
): Promise<TGActivityLogEntryWithSubcontractor[]> {
  let query = supabase
    .from('tg_activity_log')
    .select(`
      *,
      subcontractors(name, email)
    `)
    .order('created_at', { ascending: false })

  if (options?.subcontractorId) {
    query = query.eq('subcontractor_id', options.subcontractorId)
  }
  if (options?.actionType) {
    query = query.eq('action_type', options.actionType)
  }
  if (options?.pageName) {
    query = query.eq('page_name', options.pageName)
  }
  if (options?.startDate) {
    query = query.gte('created_at', options.startDate)
  }
  if (options?.endDate) {
    query = query.lte('created_at', options.endDate)
  }

  query = query.limit(options?.limit || 100)
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 100) - 1)
  }

  const { data, error } = await query

  if (error) throw error

  return (data || []).map(entry => ({
    ...entry,
    subcontractor_name: (entry.subcontractors as { name: string; email: string } | null)?.name || 'Okänd',
    subcontractor_email: (entry.subcontractors as { name: string; email: string } | null)?.email || '',
    subcontractors: undefined
  })) as TGActivityLogEntryWithSubcontractor[]
}

export async function getTGActivitySummary(): Promise<TGActivitySummary[]> {
  // Get all non-admin subcontractors
  const { data: subcontractors, error: subError } = await supabase
    .from('subcontractors')
    .select('id, name, is_active')
    .eq('is_admin', false)
    .order('name')

  if (subError) throw subError

  const summaries: TGActivitySummary[] = []

  for (const sub of subcontractors || []) {
    // Get all activity for this subcontractor
    const { data: activities, error: actError } = await supabase
      .from('tg_activity_log')
      .select('action_type, page_name, created_at')
      .eq('subcontractor_id', sub.id)
      .order('created_at', { ascending: false })

    if (actError) throw actError

    // Count visits per page
    const pageVisitCounts: Record<TGPageName, number> = {
      dashboard: 0,
      calendar: 0,
      assignments: 0,
      assignment_detail: 0,
      articles: 0,
      handbook: 0,
      profile: 0
    }

    let lastLogin: string | null = null
    let lastActivity: string | null = null
    let loginCount = 0

    for (const activity of activities || []) {
      if (!lastActivity) {
        lastActivity = activity.created_at
      }

      if (activity.action_type === 'login') {
        loginCount++
        if (!lastLogin) {
          lastLogin = activity.created_at
        }
      }

      if (activity.action_type === 'page_view' && activity.page_name) {
        const pageName = activity.page_name as TGPageName
        if (pageName in pageVisitCounts) {
          pageVisitCounts[pageName]++
        }
      }
    }

    const totalVisits = Object.values(pageVisitCounts).reduce((a, b) => a + b, 0)

    summaries.push({
      subcontractor_id: sub.id,
      subcontractor_name: sub.name,
      is_active: sub.is_active,
      total_visits: totalVisits,
      total_logins: loginCount,
      last_login: lastLogin,
      last_activity: lastActivity,
      page_visits: pageVisitCounts
    })
  }

  return summaries
}

// ============================================
// Helper functions
// ============================================

export function getPageNameLabel(pageName: TGPageName): string {
  return PAGE_NAME_LABELS[pageName] || pageName
}

export function getActionTypeLabel(actionType: TGActionType): string {
  switch (actionType) {
    case 'login':
      return 'Inloggning'
    case 'page_view':
      return 'Sidvisning'
    default:
      return actionType
  }
}
